import { createContext, useContext, ReactNode, useEffect, useState, useMemo } from 'react';
import { useQuery, ApolloClient } from '@apollo/client';
import { useLocale } from '../../../../../contexts/LocaleProvider';
import {
    GET_RESOURCE_LIBRARY_ARTICLE_ITEMS_QUERY,
    getResourceLibraryArticleItemsQueryVariables,
} from '../queries/getResourceLibraryArticleItemsQuery';

import { ResourceLibraryArticleBaseItem } from '../types/resourceLibraryItems';
import { transformResourceLibraryArticleItemsResponse } from '../tools/transformResourceLibraryArticleItemsResponse';
import { GetResourceLibraryArticleItemListingQuery } from '../schemas/generated-types/graphql';
import { useQueryFallbackResponse } from '../../../common/hooks/useQueryFallbackResponse';

interface ResourceLibraryArticleItemContextValue {
    items: ResourceLibraryArticleBaseItem[];
    loading: boolean;
    error: any;
    refetch: () => void; // refetches all Resource Library Items
    initialize: () => void;
}

const ResourceLibraryArticleItemContext = createContext<
    ResourceLibraryArticleItemContextValue | undefined
>(undefined);

interface ResourceLibraryArticleItemProviderProps {
    children: ReactNode;
    apolloClient: ApolloClient<any>;
}

const ResourceLibraryArticleItemsProvider = ({
    children,
    apolloClient,
}: ResourceLibraryArticleItemProviderProps) => {
    const { locale } = useLocale();

    const [initialized, setInitialized] = useState(false);

    // load ArticleItems
    const query = GET_RESOURCE_LIBRARY_ARTICLE_ITEMS_QUERY;
    const {
        loading: loadingQuery,
        error: queryError,
        data: queryData,
    } = useQuery<GetResourceLibraryArticleItemListingQuery>(query, {
        variables: getResourceLibraryArticleItemsQueryVariables(locale),
        client: apolloClient,
        errorPolicy: 'all', // Allow partial data instead of full rejection
        skip: !initialized, // Do not run query until initialization is triggered.
    });

    const {
        fallbackData,
        loading: loadingFallback,
        error: fallbackError,
    } = useQueryFallbackResponse({ query, locale, initialized, queryError: queryError });

    const refetch = () => {
        if (initialized) {
            apolloClient.refetchQueries({
                include: 'active',
            });
        } else {
            initialize();
        }
    };

    // Transform data when query is completed
    const resourceLibraryArticleItems = useMemo(() => {
        const data = queryData || fallbackData;
        if (!data) return [];
        return transformResourceLibraryArticleItemsResponse(data);
    }, [queryData, fallbackData]);

    const resourceLibraryItems = useMemo(() => {
        const resourceLibraryItems: ResourceLibraryArticleBaseItem[] = [];
        resourceLibraryItems.push(...resourceLibraryArticleItems);
        return resourceLibraryItems;
    }, [resourceLibraryArticleItems]);

    const initialize = () => {
        if (!initialized) {
            setInitialized(true);
        }
    };

    const loading = loadingQuery || loadingFallback;
    // if we have fallback data, we don't consider it an error
    const error = fallbackError || (queryError && !fallbackData ? queryError : null);

    return (
        <ResourceLibraryArticleItemContext.Provider
            value={{
                items: resourceLibraryItems || [],
                loading: loading,
                error: error,
                refetch,
                initialize,
            }}
        >
            {children}
        </ResourceLibraryArticleItemContext.Provider>
    );
};

// Unsafe hook: does not trigger initialization.
function useResourceLibraryArticleItemsUnsafe() {
    const context = useContext(ResourceLibraryArticleItemContext);
    if (!context) {
        throw new Error(
            'useResourceLibraryArticleItems must be used within a ResourceLibraryArticleItemsProvider'
        );
    }
    return context;
}

// Safe hook: triggers initialization for this provider.
function useResourceLibraryArticleItems() {
    const context = useResourceLibraryArticleItemsUnsafe();
    useEffect(() => {
        context.initialize();
    }, [context]);
    return context;
}

export {
    ResourceLibraryArticleItemsProvider,
    useResourceLibraryArticleItems,
    useResourceLibraryArticleItemsUnsafe,
};
export type { ResourceLibraryArticleItemContextValue };
